/**
 * PKCE auth flow using Zoom SDK authorize() + onAuthorized().
 *
 * Flow:
 *   1. Generate code_verifier + code_challenge (S256)
 *   2. Call zoomSdk.authorize({ codeChallenge, codeChallengeMethod: 'S256' })
 *   3. Listen for onAuthorized callback with { code }
 *   4. Exchange code + code_verifier on our backend for a session token
 */

import zoomSdk from '@zoom/appssdk';
import { exchangeToken, type ExchangeOutcome, type LinkedSession } from './api';

let codeVerifier: string | null = null;

/* ------------------------------------------------------------------ */
/*  PKCE helpers                                                      */
/* ------------------------------------------------------------------ */

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  return crypto.subtle.digest('SHA-256', encoder.encode(plain));
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const hash = await sha256(verifier);
  return base64UrlEncode(hash);
}

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */

/** A successful exchange: the meeting has a linked session. */
export type ZoomAuthResult = LinkedSession;

/**
 * What startZoomAuth resolves to: a linked session, or an `unlinked` status
 * when nothing is linked to the meeting yet (the app then shows the host
 * session picker or a waiting screen instead of an error).
 */
export type ZoomAuthOutcome = ExchangeOutcome;

/**
 * Initiate the Zoom PKCE authorization flow.
 * Returns a promise that resolves when the token exchange completes.
 *
 * @param meetingId  The Zoom meeting ID (links the session).
 * @param role       The caller's Zoom role ('host' | 'coHost' | 'attendee');
 *                   the backend uses it to decide facilitator vs participant.
 */
export async function startZoomAuth(
  meetingId: string,
  role: string,
): Promise<ZoomAuthOutcome> {
  // Step 1: Generate PKCE pair
  codeVerifier = generateRandomString(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Step 2: Start the authorize flow
  return new Promise<ZoomAuthOutcome>((resolve, reject) => {
    // Step 3: Listen for the authorization callback
    const handleAuthorized = async (event: { code: string }) => {
      try {
        if (!codeVerifier) {
          throw new Error('Code verifier is missing');
        }

        // Step 4: Exchange the code for a session token
        const result = await exchangeToken(
          event.code,
          codeVerifier,
          meetingId,
          role,
        );
        codeVerifier = null;
        zoomSdk.removeEventListener('onAuthorized', handleAuthorized);

        resolve(result);
      } catch (err) {
        codeVerifier = null;
        zoomSdk.removeEventListener('onAuthorized', handleAuthorized);
        reject(err);
      }
    };

    // The flow can run more than once per app instance ("Check again" on the
    // unlinked screen, host token expiry), so each run removes its listener
    // when it settles rather than stacking handlers.
    zoomSdk.addEventListener('onAuthorized', handleAuthorized);

    zoomSdk.authorize({
      codeChallenge,
    } as Parameters<typeof zoomSdk.authorize>[0]).catch((err: Error) => {
      codeVerifier = null;
      zoomSdk.removeEventListener('onAuthorized', handleAuthorized);
      reject(err);
    });
  });
}

/**
 * Get the current code verifier (for debugging).
 */
export function getCodeVerifier(): string | null {
  return codeVerifier;
}
